import React from 'react';
import { ButtonContainer, MainPart, DropdownPart, LeftImage, RightImage } from './style';

/**
 * The pill submit button, optionally SPLIT: main body plus a dropdown segment.
 *
 * With `onDropdownClick` the two halves become independent buttons inside a plain
 * container — clicking the arrow must not submit. Without it the component is exactly
 * what it was: one button, unchanged for every existing caller.
 */
const RoundedBorderSubmitBtn = React.forwardRef(({
  leftIcon = null,
  rightIcon = null,
  text,
  onClickFn,
  onDropdownClick = null,
  dropdownLabel = 'More options',
  // id of the element the dropdown opens, exposed as aria-controls on the arrow so the
  // opened panel can tell the arrow apart from any other outside click.
  dropdownControls = null,
  isDropdownOpen = false,
  isDisabled = false,
  // The dropdown is deliberately INDEPENDENT of the submit half: attaching course
  // materials to a draft has nothing to do with whether the response is long enough
  // to submit, so a disabled Submit must not lock the teacher out of the menu.
  isDropdownDisabled = false,
  fullWidth = false,
  // id of an element explaining the button's state, set on the submit half.
  ariaDescribedBy = undefined,
}, ref) => {
  const isSplit = Boolean(rightIcon && onDropdownClick);

  const handleClick = (event) => {
    if (isDisabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    onClickFn?.(event);
  };

  const handleDropdownClick = (event) => {
    // Never let the arrow reach the submit handler — that would fire a submission the
    // teacher did not ask for.
    event.preventDefault();
    event.stopPropagation();
    if (isDropdownDisabled) return;
    onDropdownClick?.(event);
  };

  return (
    <ButtonContainer
      ref={ref}
      as={isSplit ? 'div' : 'button'}
      onClick={isSplit ? undefined : handleClick}
      disabled={isSplit ? undefined : isDisabled}
      aria-describedby={isSplit ? undefined : ariaDescribedBy}
      $disabled={isDisabled}
      $split={isSplit}
      fullWidth={fullWidth}
    >
      <MainPart
        as={isSplit ? 'button' : 'span'}
        type={isSplit ? 'button' : undefined}
        onClick={isSplit ? handleClick : undefined}
        disabled={isSplit ? isDisabled : undefined}
        aria-describedby={isSplit ? ariaDescribedBy : undefined}
        $hasDropdown={!!rightIcon}
        $disabled={isDisabled}
        $split={isSplit}
      >
        {leftIcon && <LeftImage src={leftIcon} alt="" $split={isSplit} />}
        {text}
      </MainPart>
      {rightIcon && (
        <DropdownPart
          as={isSplit ? 'button' : 'span'}
          type={isSplit ? 'button' : undefined}
          onClick={isSplit ? handleDropdownClick : undefined}
          disabled={isSplit ? isDropdownDisabled : undefined}
          aria-label={isSplit ? dropdownLabel : undefined}
          aria-haspopup={isSplit ? 'dialog' : undefined}
          aria-controls={isSplit && dropdownControls ? dropdownControls : undefined}
          aria-expanded={isSplit ? isDropdownOpen : undefined}
          $disabled={isSplit ? isDropdownDisabled : isDisabled}
          $split={isSplit}
        >
          <RightImage src={rightIcon} alt="" $split={isSplit} />
        </DropdownPart>
      )}
    </ButtonContainer>
  );
});

export default RoundedBorderSubmitBtn;
